/**
 * Lớp gọi API dùng chung cho các nhóm màn hình bản tin, kho âm thanh, lịch phát
 * và phát sóng (Đợt 2, phần B4) — cùng khuôn với `thiet-bi-api.ts` của A4: một
 * chỗ duy nhất xử lý phong bì phản hồi, token và huỷ yêu cầu (AbortSignal).
 *
 * Không định nghĩa lại `LoiApi`: dùng lại đúng lớp của A4 để mọi nơi bắt lỗi
 * bằng `instanceof LoiApi` đều nhận diện được, dù lỗi đến từ nhóm màn hình nào.
 */
import { layToken, type PhongBiNoiBo } from './khach-api';
import { LoiApi } from './thiet-bi-api';

export { LoiApi };

function goc(): string {
  return (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
}

export interface TuyChonGoi {
  phuongThuc?: string;
  than?: unknown;
  signal?: AbortSignal;
}

export async function goi<T>(duongDan: string, tuyChon: TuyChonGoi = {}): Promise<T> {
  const token = layToken();
  const res = await fetch(`${goc()}${duongDan}`, {
    method: tuyChon.phuongThuc ?? 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tuyChon.than !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: tuyChon.than !== undefined ? JSON.stringify(tuyChon.than) : undefined,
    signal: tuyChon.signal,
  });
  let pb: PhongBiNoiBo<T>;
  try {
    pb = (await res.json()) as PhongBiNoiBo<T>;
  } catch {
    throw new LoiApi('INTERNAL_ERROR', 'Máy chủ trả về dữ liệu không đọc được');
  }
  if (!pb.success) throw new LoiApi(pb.code, pb.message, pb.requestId);
  return pb.data as T;
}

/**
 * Tải một tệp do máy chủ dựng (vd .xlsx) và lưu về máy người dùng. Trả số dòng đã xuất và tổng
 * số dòng khớp bộ lọc (header `X-So-Dong`, `X-Tong`) để màn hình báo kết quả / vượt giới hạn.
 */
export async function taiTepTuApi(
  duongDan: string,
  tenMacDinh: string,
): Promise<{ soDong: number; tong: number }> {
  const token = layToken();
  const res = await fetch(`${goc()}${duongDan}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const pb = (await res.json().catch(() => null)) as { code?: string; message?: string } | null;
    throw new LoiApi(pb?.code ?? 'INTERNAL_ERROR', pb?.message ?? `Máy chủ trả lỗi ${res.status}`);
  }
  const ten = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? tenMacDinh;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = ten;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  const soDong = Number(res.headers.get('x-so-dong') ?? 0);
  return { soDong, tong: Number(res.headers.get('x-tong') ?? soDong) };
}

export function chuoiTruyVan(tham: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(tham)) {
    if (v === undefined || v === null || v === '') continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}
